/**
 * PeriodLocksTab — Bloqueo manual de períodos contables.
 *
 * Permite bloquear rangos de fechas para que los comprobantes contables
 * con `date` dentro del rango sean rechazados. El bloqueo es soft: se
 * desactiva en lugar de borrarse, queda historial de quien bloqueó/desbloqueó
 * y razones.
 */
import { EmptyState } from '@/components/EmptyState'
import { DataTable } from '@/components/ds/DataTable'
import { useState } from 'react'
import { Plus, Lock, Unlock, Loader2, FileText } from 'lucide-react'
import {
  usePeriodLocks, useCreatePeriodLock, useUnlockPeriod,
  type PeriodLock,
} from '../hooks/useAccounting'
import { usePermission } from '@/hooks/usePermission'
import { ToastContainer, useToast } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'
import { useTenantLocale, formatDate } from '@/lib/locale'
import { Modal } from '@/components/ds/Modal'

type Filter = 'active' | 'inactive' | 'all'

export function PeriodLocksTab() {
  const [filter, setFilter] = useState<Filter>('active')
  const [createOpen, setCreateOpen] = useState(false)
  const [unlocking, setUnlocking] = useState<PeriodLock | null>(null)
  const { can } = usePermission()
  const locale = useTenantLocale()
  const toast = useToast()

  const { data, isLoading } = usePeriodLocks({
    active: filter === 'all' ? undefined : filter === 'active',
  })
  const locks = data?.locks ?? []

  return (
    <>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <select aria-label="Filtrar períodos"
          value={filter}
          onChange={e => setFilter(e.target.value as Filter)}
          className="px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content"
        >
          <option value="active">Activos</option>
          <option value="inactive">Desactivados</option>
          <option value="all">Todos</option>
        </select>
        {can('accounting.period_lock', 'create') && (
          <button
            onClick={() => setCreateOpen(true)}
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover"
          >
            <Plus size={14} /> Bloquear período
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-divider">
        {isLoading ? (
          <div className="px-4 py-8 text-center text-sm text-content-subtle">Cargando…</div>
        ) : locks.length === 0 ? (
          <EmptyState
            icon={Lock}
            size="sm"
            title={`No hay bloqueos ${filter === 'active' ? 'activos' : filter === 'inactive' ? 'desactivados' : 'registrados'}.`}
          />
        ) : (
          <DataTable
            columns={[
              {
                key: 'desde', header: 'Desde', sortable: true, sortValue: l => l.start_date,
                render: l => <span className="font-mono">{formatDate(l.start_date, locale)}</span>,
              },
              {
                key: 'hasta', header: 'Hasta', sortable: true, sortValue: l => l.end_date,
                render: l => <span className="font-mono">{formatDate(l.end_date, locale)}</span>,
              },
              {
                key: 'motivo', header: 'Motivo', sortable: true, sortValue: l => l.reason,
                render: l => <div className="max-w-xs truncate" title={l.reason}>{l.reason}</div>,
              },
              {
                key: 'estado', header: 'Estado', sortable: true, sortValue: l => (l.is_active ? 'Bloqueado' : 'Desbloqueado'),
                render: l => l.is_active ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-warning-surface-strong text-warning-content">
                    <Lock size={11} /> Bloqueado
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-surface-muted text-content-muted">
                    <Unlock size={11} /> Desbloqueado
                  </span>
                ),
              },
              {
                key: 'bloqueado_por', header: 'Bloqueado por',
                render: l => (
                  <div className="text-xs text-content-subtle">
                    <div>{l.locked_by_email || '—'}</div>
                    <div>{formatDate(l.locked_at?.slice(0, 10) || '', locale)}</div>
                  </div>
                ),
              },
              {
                key: 'desbloqueado_por', header: 'Desbloqueado por',
                render: l => l.unlocked_at ? (
                  <div className="text-xs text-content-subtle">
                    <div>{l.unlocked_by_email || '—'}</div>
                    <div>{formatDate(l.unlocked_at.slice(0, 10), locale)}</div>
                    {l.unlock_reason && (
                      <div className="text-content-muted italic mt-0.5" title={l.unlock_reason}>
                        "{l.unlock_reason.slice(0, 30)}{l.unlock_reason.length > 30 ? '…' : ''}"
                      </div>
                    )}
                  </div>
                ) : '—',
              },
              {
                key: 'acciones', header: 'Acciones',
                render: l => l.is_active && can('accounting.period_lock', 'edit') ? (
                  <button
                    onClick={() => setUnlocking(l)}
                    title="Desbloquear período"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-warning-content border border-warning-border rounded-lg hover:bg-warning-surface"
                  >
                    <Unlock size={12} /> Desbloquear
                  </button>
                ) : null,
              },
            ]}
            rows={locks}
            minWidth={900}
            caption="Períodos contables bloqueados"
          />
        )}
      </div>

      {createOpen && (
        <CreateLockModal onClose={() => setCreateOpen(false)} onSuccess={() => toast.success('Período bloqueado.')} onError={(m) => toast.error(m)} />
      )}
      {unlocking && (
        <UnlockModal lock={unlocking} onClose={() => setUnlocking(null)} onSuccess={() => toast.success('Período desbloqueado.')} onError={(m) => toast.error(m)} />
      )}

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

function CreateLockModal({ onClose, onSuccess, onError }: {
  onClose: () => void
  onSuccess: () => void
  onError: (msg: string) => void
}) {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')
  const create = useCreatePeriodLock()

  const handleSave = () => {
    if (!startDate || !endDate || !reason.trim()) return
    create.mutate(
      { start_date: startDate, end_date: endDate, reason: reason.trim(), notes: notes.trim() || undefined },
      {
        onSuccess: () => { onSuccess(); onClose() },
        onError: (e: any) => onError(extractErrorMessage(e, 'Error al bloquear período')),
      }
    )
  }

  return (
    <Modal open onClose={onClose} title={<span className="flex items-center gap-2"><Lock size={16} /> Bloquear período</span>} size="md"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={create.isPending || !startDate || !endDate || !reason.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
        >
          {create.isPending && <Loader2 size={14} className="animate-spin" />}
          Bloquear período
        </button>
      </>}
    >
      <div className="space-y-4">
        <p className="text-xs text-content-muted">
          Una vez bloqueado, no se podrán crear ni modificar comprobantes contables con fecha dentro del rango.
          El bloqueo se puede revertir más adelante con razón documentada.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="periodlockstab-desde" className="block text-xs font-medium text-content-muted mb-1">Desde *</label>
            <input id="periodlockstab-desde"
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="periodlockstab-hasta" className="block text-xs font-medium text-content-muted mb-1">Hasta *</label>
            <input id="periodlockstab-hasta"
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
        <div>
          <label htmlFor="periodlockstab-motivo" className="block text-xs font-medium text-content-muted mb-1">Motivo *</label>
          <textarea id="periodlockstab-motivo"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Ej. Cierre del ejercicio 2024 — auditoría finalizada"
            rows={2}
            className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content resize-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label htmlFor="periodlockstab-notas-opcional" className="block text-xs font-medium text-content-muted mb-1">Notas (opcional)</label>
          <textarea id="periodlockstab-notas-opcional"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Detalle adicional para auditoría"
            rows={2}
            className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content resize-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>
    </Modal>
  )
}

function UnlockModal({ lock, onClose, onSuccess, onError }: {
  lock: PeriodLock
  onClose: () => void
  onSuccess: () => void
  onError: (msg: string) => void
}) {
  const [reason, setReason] = useState('')
  const unlock = useUnlockPeriod(lock.id)

  const handleSave = () => {
    if (!reason.trim()) return
    unlock.mutate(
      { reason: reason.trim() },
      {
        onSuccess: () => { onSuccess(); onClose() },
        onError: (e: any) => onError(extractErrorMessage(e, 'Error al desbloquear')),
      }
    )
  }

  return (
    <Modal open onClose={onClose} title={<span className="flex items-center gap-2"><Unlock size={16} /> Desbloquear período</span>} size="md"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={unlock.isPending || !reason.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-warning-fill text-white rounded-lg hover:bg-warning-fill-hover disabled:opacity-50"
        >
          {unlock.isPending && <Loader2 size={14} className="animate-spin" />}
          Desbloquear
        </button>
      </>}
    >
      <div className="space-y-4">
        <div className="bg-warning-surface border border-warning-border rounded-lg px-3 py-2 text-sm text-warning-content-strong">
          <div className="flex items-start gap-2">
            <FileText size={14} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Período bloqueado:</p>
              <p className="text-xs mt-1">{lock.start_date} → {lock.end_date}</p>
              <p className="text-xs italic mt-1">"{lock.reason}"</p>
            </div>
          </div>
        </div>
        <div>
          <label htmlFor="periodlockstab-motivo-del-desbloqueo" className="block text-xs font-medium text-content-muted mb-1">Motivo del desbloqueo *</label>
          <textarea id="periodlockstab-motivo-del-desbloqueo"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Ej. Ajuste tardío autorizado por contador externo (caso XYZ)"
            rows={3}
            className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content resize-none focus:ring-2 focus:ring-primary"
          />
          <p className="mt-1 text-xs text-content-subtle">
            Queda registrado en el historial junto con tu usuario y fecha actual.
          </p>
        </div>
      </div>
    </Modal>
  )
}
